import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Plus, Trash, Sparkle } from "@phosphor-icons/react";
import { useState } from "react";

export interface SessionEntry {
  // Sarajevo wall-clock for the form. We convert to UTC ISO at submit time.
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
}

interface Props {
  sessions: SessionEntry[];
  onChange: (next: SessionEntry[]) => void;
}

const WEEK_OPTIONS = [
  { label: "Sedmično", weeks: 1 },
  { label: "Svake 2 sedmice", weeks: 2 },
  { label: "Svake 3 sedmice", weeks: 3 },
];

export function SessionScheduleBuilder({ sessions, onChange }: Props) {
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("18:00");
  const [count, setCount] = useState(4);
  const [intervalWeeks, setIntervalWeeks] = useState(1);

  const generate = () => {
    if (!startDate) return;
    const [y, m, d] = startDate.split("-").map(Number) as [number, number, number];
    const next: SessionEntry[] = [];
    for (let i = 0; i < count; i++) {
      const date = new Date(Date.UTC(y, m - 1, d + i * intervalWeeks * 7));
      const yy = date.getUTCFullYear();
      const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(date.getUTCDate()).padStart(2, "0");
      next.push({ date: `${yy}-${mm}-${dd}`, time: startTime });
    }
    onChange(next);
  };

  const updateOne = (idx: number, patch: Partial<SessionEntry>) => {
    onChange(sessions.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const remove = (idx: number) => {
    onChange(sessions.filter((_, i) => i !== idx));
  };

  const add = () => {
    onChange([...sessions, { date: "", time: startTime }]);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dashed border-[#d6cfc1] bg-[#fbf7f0] p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
          <Sparkle className="size-4" weight="fill" />
          Generiši termine automatski
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">Početni datum</Label>
            <DatePicker value={startDate} onChange={setStartDate} placeholder="Početni datum" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Vrijeme</Label>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Broj termina</Label>
            <Input
              type="number"
              min={1}
              max={52}
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value) || 1)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Ponavljanje</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
              value={intervalWeeks}
              onChange={(e) => setIntervalWeeks(parseInt(e.target.value))}
            >
              {WEEK_OPTIONS.map((o) => (
                <option key={o.weeks} value={o.weeks}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Button type="button" variant="outline" className="mt-3" onClick={generate}>
          Generiši
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Termini ({sessions.length})</Label>
          <Button type="button" variant="ghost" size="sm" onClick={add}>
            <Plus className="size-4" /> Dodaj termin
          </Button>
        </div>
        {sessions.length === 0 && (
          <p className="rounded-md border border-dashed border-[#e6dfcf] p-4 text-center text-sm text-muted-foreground">
            Nema dodanih termina. Koristi automatsko generisanje ili dodaj ručno.
          </p>
        )}
        {sessions.map((s, idx) => (
          <div key={idx} className="flex items-center gap-2 rounded-md border border-[#ece8df] p-2">
            <span className="w-6 text-xs text-muted-foreground">{idx + 1}.</span>
            <DatePicker
              value={s.date}
              onChange={(date) => updateOne(idx, { date })}
              placeholder="Datum termina"
              className="flex-1"
            />
            <Input
              type="time"
              value={s.time}
              onChange={(e) => updateOne(idx, { time: e.target.value })}
              className="w-32"
            />
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(idx)}>
              <Trash className="size-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
